/**
 * Copyright 2022-2025 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { Else, If, Then } from "react-if";
import {
  SVGIcon,
  Box,
  theme,
  ExtendedTooltip,
  Text,
  SVGIconProps,
} from "@nordcloud/gnui";
import { CloudResourceProvider, ResourceStatus } from "~/generated/graphql";
import {
  getCloudResourceColorContext,
  getCloudResourceStatusIconName,
  getCloudResourceStatusMessage,
} from "~/views/resources_v2/utils";
import { getProviderIcon } from "../utils";

type CloudResourceStatusProps = {
  status: ResourceStatus;
  iconSize?: SVGIconProps["size"];
  boxes?: boolean;
};

export function CloudResourceStatus({
  status,
  iconSize,
  boxes,
}: CloudResourceStatusProps) {
  return (
    <>
      <ExtendedTooltip
        status={getCloudResourceColorContext(status)}
        caption={
          <Text tag="span" size="sm" color={theme.color.text.text04}>
            {getCloudResourceStatusMessage(status)}
          </Text>
        }
        position="start"
      >
        <IconBox
          icon={getCloudResourceStatusIconName(status)}
          iconSize={iconSize}
          color={getCloudResourceColorContext(status)}
          boxEnabled={boxes}
        />
      </ExtendedTooltip>
    </>
  );
}

type CloudResourceProviderIconProps = {
  cloud: CloudResourceProvider;
  iconSize?: SVGIconProps["size"];
  boxes?: boolean;
};

export function CloudResourceProviderIcon({
  cloud,
  iconSize,
  boxes,
}: CloudResourceProviderIconProps) {
  const toolTipText = cloud || "No Data Available";

  return (
    <>
      <ExtendedTooltip caption={toolTipText} position="start">
        <IconBox
          icon={getProviderIcon(cloud)}
          iconSize={iconSize}
          boxEnabled={boxes}
        />
      </ExtendedTooltip>
    </>
  );
}

type IconBoxProps = {
  icon: SVGIconProps["name"];
  iconSize: SVGIconProps["size"];
  color?: SVGIconProps["color"];
  boxEnabled?: boolean;
};

function IconBox({ icon, iconSize, color, boxEnabled }: IconBoxProps) {
  const svgIcon = <SVGIcon size={iconSize ?? "lg"} name={icon} color={color} />;

  return (
    <If condition={boxEnabled}>
      <Then>
        <Box boxStyle="lightGrey" mb={theme.spacing.spacing04}>
          {svgIcon}
        </Box>
      </Then>
      <Else>{svgIcon}</Else>
    </If>
  );
}
